import {
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import SwiperJs from "swiper";
import { FreeMode, Pagination } from "swiper/modules";
import type { SwiperOptions } from "swiper/types";
import * as dimensionUtils from "@/common/utils/dimensionUtils";
import {
  SLIDE_IMAGE_CLASS,
  SWIPER_SLIDE,
} from "@/components/shared/swiperSlideClass";
import type { Size } from "@/common/utils/dimensionUtils";
import * as sizeManager from "@/modules/sizeManager";

import LeftIcon from "@svg/left.svg";
import RightIcon from "@svg/right.svg";

// Swiper 14 defaults that differ from the 4.x behaviour the player was built on, set back.
const DEFAULT_CONFIG: SwiperOptions = {
  modules: [Pagination, FreeMode],
  containerModifierClass: "swiper-container-",
  touchEventsTarget: "container",
  threshold: 0,
  touchMoveStopPropagation: true,
  watchOverflow: false,
  // A pagination selector outside the container must resolve against the document, as in 4.x.
  uniqueNavElements: false,
  resizeObserver: false,
  maxBackfaceHiddenSlides: 0,
};

type SwiperProps = {
  index?: number;
  config?: SwiperOptions;
  images?: string[];
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  nodeRef?: RefObject<HTMLDivElement | null>;
  hasButtons?: boolean;
  previousActive?: (index: number | undefined, items: ReactNode) => boolean;
  nextActive?: (index: number | undefined, items: ReactNode) => boolean;
  onTouchStart?: () => void;
  onTouchEnd?: () => void;
  onTransitionStart?: (index: number) => void;
  onTransitionEnd?: (index: number) => void;
  onItemChanged?: (index: number) => void;
  onIncrementItem?: () => void;
  onDecrementItem?: () => void;
};

interface ItemSource {
  images?: string[];
  children?: ReactNode;
}

const lengthOf = (value: unknown) => (value as ArrayLike<unknown>).length;

/**
 * Looks at each slide or image and reports whether the items differ from the
 * previous render. Only compares when the item count is unchanged (a known limit).
 */
function didChildrenChange(prev: ItemSource, cur: ItemSource): boolean {
  let old: unknown;
  let b: unknown;
  let isString = false;
  if (cur.images !== undefined) {
    old = prev.images;
    b = cur.images;
    isString = true;
  } else if (cur.children !== undefined) {
    old = prev.children;
    b = cur.children;
  }

  if (old === b || (old == null && b == null)) {
    return false;
  }
  if (old == null || b == null) {
    return true;
  }
  if (lengthOf(old) !== lengthOf(b)) {
    return false;
  }

  if (isString) {
    for (let m = 0, maxm = lengthOf(b); m < maxm; m++) {
      // biome-ignore lint/suspicious/noDoubleEquals: null and undefined entries must compare equal
      if ((old as string[])[m] != (b as string[])[m]) {
        return true;
      }
    }
  } else {
    return false;
  }

  return true;
}

// Copied from Swiper's core updateSize: the container size less its padding.
function getSwiperContainerSize(swiper: SwiperJs): Size | undefined {
  const el = swiper.el;
  const width =
    typeof swiper.params.width === "number"
      ? swiper.params.width
      : el.clientWidth;
  const height =
    typeof swiper.params.height === "number"
      ? swiper.params.height
      : el.clientHeight;
  if (
    (width === 0 && swiper.isHorizontal()) ||
    (height === 0 && swiper.isVertical())
  ) {
    return;
  }

  const style = getComputedStyle(el);
  return {
    width:
      width -
      parseInt(style.paddingLeft, 10) -
      parseInt(style.paddingRight, 10),
    height:
      height -
      parseInt(style.paddingTop, 10) -
      parseInt(style.paddingBottom, 10),
  };
}

// Arrows in the .articles and favorite image swipers: hidden at rest (clip) but still a Tab stop, and taps go through to the slide
const ARROW_CLASS =
  "tw:[.favorite-image-slider-swiper_&]:absolute tw:[.favorite-image-slider-swiper_&]:top-[50%] tw:[.favorite-image-slider-swiper_&]:z-1 tw:[.favorite-image-slider-swiper_&]:h-[100px] tw:[.favorite-image-slider-swiper_&]:[transform:translateY(-50%)] tw:[.favorite-image-slider-swiper_&]:[clip-path:inset(50%)] tw:[.articles_&]:absolute tw:[.articles_&]:top-[50%] tw:[.articles_&]:z-1 tw:[.articles_&]:h-[100px] tw:[.articles_&]:[transform:translateY(-50%)] tw:[.articles_&]:[clip-path:inset(50%)]";

export default function Swiper({
  index,
  config,
  images,
  children,
  className,
  style,
  nodeRef,
  hasButtons,
  previousActive,
  nextActive,
  onTouchStart,
  onTouchEnd,
  onTransitionStart,
  onTransitionEnd,
  onItemChanged,
  onIncrementItem,
  onDecrementItem,
}: SwiperProps) {
  const [hover, setHover] = useState(false);

  const divRef = useRef<HTMLDivElement | null>(null);
  const swiperRef = useRef<SwiperJs | null>(null);
  const lastIndex = useRef(index);
  const touch = useRef(false);
  const isTransitioning = useRef(false);
  const isTouching = useRef(false);
  const resizeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevItems = useRef<ItemSource | null>(null);

  const checkIndex = () => {
    const swiper = swiperRef.current;
    if (
      swiper &&
      index != null &&
      index !== swiper.activeIndex &&
      !isTransitioning.current &&
      !isTouching.current
    ) {
      // An unchanged index pops to the slide; a changed one animates.
      if (index === lastIndex.current) {
        swiper.slideTo(index, 0, false);
      } else {
        lastIndex.current = index;
        swiper.slideTo(index, undefined, false);
      }
    }
  };

  // Swiper keeps these listeners from mount; they must see the latest props and state.
  const onSwiperTouchStart = useEffectEvent(() => {
    isTouching.current = true;
    if (onTouchStart) {
      onTouchStart();
    }
  });

  const onSwiperTouchEnd = useEffectEvent(() => {
    isTouching.current = false;
    if (onTouchEnd) {
      onTouchEnd();
    }
  });

  const onSwiperTransitionStart = useEffectEvent(() => {
    const s = swiperRef.current as SwiperJs;
    if (!isTransitioning.current) {
      isTransitioning.current = true;
      if (onTransitionStart) {
        onTransitionStart(s.activeIndex);
      }
    }
  });

  const onSwiperTransitionEnd = useEffectEvent(() => {
    const s = swiperRef.current as SwiperJs;
    isTransitioning.current = false;
    if (onItemChanged && s.activeIndex !== index) {
      onItemChanged(s.activeIndex);
    }
    if (onTransitionEnd) {
      onTransitionEnd(s.activeIndex);
    }

    lastIndex.current = s.activeIndex;
  });

  const onMouseEnter = useEffectEvent(() => {
    if (touch.current === false && hover === false) {
      setHover(true);
    }
  });

  const onMouseLeave = useEffectEvent(() => {
    if (hover === true) {
      setHover(false);
    }
  });

  const onTouchEndDom = useEffectEvent(() => {
    touch.current = true;
    if (hover === true) {
      setHover(false);
    }
  });

  const onMount = useEffectEvent(() => {
    const div = divRef.current as HTMLDivElement;
    const swiper = new SwiperJs(div, { ...DEFAULT_CONFIG, ...config });
    swiperRef.current = swiper;

    if (swiper.classNames.length === 0) {
      console.warn("Swiper did not find swiper element.");
    }

    swiper.on("touchStart", onSwiperTouchStart);
    swiper.on("touchEnd", onSwiperTouchEnd);
    swiper.on("slideChangeTransitionStart", onSwiperTransitionStart);
    swiper.on("slideChangeTransitionEnd", onSwiperTransitionEnd);

    div.addEventListener("mouseenter", onMouseEnter);
    div.addEventListener("mouseleave", onMouseLeave);
    div.addEventListener("touchend", onTouchEndDom);

    // sizeManager is the app's single window-resize source. A rotation can emit
    // several sizes, so re-slice the grid once against the settled width.
    const onSizeChange = () => {
      if (resizeTimer.current) {
        clearTimeout(resizeTimer.current);
      }
      resizeTimer.current = setTimeout(() => {
        resizeTimer.current = null;
        if (swiperRef.current) {
          swiperRef.current.update();
        }
      }, 300);
    };
    sizeManager.addListener(onSizeChange);

    checkIndex();

    lastIndex.current = index;
    touch.current = false;
    isTransitioning.current = false;

    return () => {
      swiper.off("touchStart", onSwiperTouchStart);
      swiper.off("touchEnd", onSwiperTouchEnd);
      swiper.off("slideChangeTransitionStart", onSwiperTransitionStart);
      swiper.off("slideChangeTransitionEnd", onSwiperTransitionEnd);
      swiper.destroy();
      swiperRef.current = null;

      sizeManager.removeListener(onSizeChange);
      if (resizeTimer.current) {
        clearTimeout(resizeTimer.current);
        resizeTimer.current = null;
      }

      div.removeEventListener("mouseenter", onMouseEnter);
      div.removeEventListener("mouseleave", onMouseLeave);
      div.removeEventListener("touchend", onTouchEndDom);
    };
  });
  useLayoutEffect(() => onMount(), []);

  // Runs after every update (not the mount), before paint, like componentDidUpdate.
  useLayoutEffect(() => {
    const prev = prevItems.current;
    const current = { images, children };
    prevItems.current = current;
    const swiper = swiperRef.current;
    if (!prev || !swiper) {
      return;
    }

    // didChildrenChange ignores a count change, and Swiper reads its slides only in update().
    // Count the DOM slides: children can hold non-slide nodes (the favorites picker cursor).
    const domSlides = swiper.wrapperEl.querySelectorAll(
      `:scope > .${swiper.params.slideClass}`,
    ).length;
    if (
      didChildrenChange(prev, current) ||
      swiper.slides.length !== domSlides
    ) {
      swiper.update();
    } else if (
      !dimensionUtils.sizeEquals(getSwiperContainerSize(swiper), swiper)
    ) {
      // A full update re-slices slide widths, snapGrid and pagination, not just the container size.
      swiper.update();
    }

    checkIndex();
  });

  // An inactive arrow is only aria-disabled, so it still gets clicks; ignore them.
  const onLeftClick = (e: MouseEvent, isActive: boolean) => {
    if (onDecrementItem) {
      if (isActive && isTransitioning.current === false) {
        onDecrementItem();
      }
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const onRightClick = (e: MouseEvent, isActive: boolean) => {
    if (onIncrementItem) {
      if (isActive && isTransitioning.current === false) {
        onIncrementItem();
      }
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const getImageSlides = (urls: string[]) => {
    const slides = [];

    for (let n = 0; n < urls.length; n++) {
      const slideStyle: CSSProperties = {};

      if (urls[n]) {
        slideStyle.backgroundImage = `url("${urls[n]}")`;
      }

      slides.push(
        <div className={SWIPER_SLIDE} key={`item-slide-${n}`}>
          <div
            className={`${SLIDE_IMAGE_CLASS} tw:[.favorite-image-slider-swiper_.swiper-slide>&]:h-full tw:[.favorite-image-slider-swiper_.swiper-slide>&]:bg-center tw:[.favorite-image-slider-swiper_.swiper-slide>&]:bg-contain tw:[.favorite-image-slider-swiper_.swiper-slide>&]:bg-no-repeat`}
            style={slideStyle}
          />
        </div>,
      );
    }

    return slides;
  };

  // Neither images nor children yields undefined, which the destructure below rejects.
  const getItems = (): [ReactNode, number] | undefined => {
    if (images !== undefined) {
      return [getImageSlides(images), images.length];
    } else if (children !== undefined) {
      return [children, Array.isArray(children) ? children.length : 1];
    }
  };

  const getButtons = (items: ReactNode, count: number) => {
    const isPreviousActive = previousActive
      ? previousActive(index, items)
      : (index as number) > 0;
    const isNextActive = nextActive
      ? nextActive(index, items)
      : (index as number) < count - 1;

    // swiper-no-swiping: a press on an arrow must not start a drag, which can
    // settle on another slide and swallow the click (Swiper's preventClicks).
    const previousClasses = [
      "left",
      "button",
      "tall",
      "swiper-no-swiping",
      ARROW_CLASS,
      "tw:[.articles_&]:left-[0px] tw:[.favorite-image-slider-swiper_&]:left-[0px]",
    ];

    if (isPreviousActive) {
      previousClasses.push("active");
      previousClasses.push("hover");
    } else {
      previousClasses.push("inactive");
    }

    const nextClasses = [
      "right",
      "button",
      "tall",
      "swiper-no-swiping",
      ARROW_CLASS,
      "tw:[.articles_&]:right-[0px] tw:[.favorite-image-slider-swiper_&]:right-[0px]",
    ];

    if (isNextActive) {
      nextClasses.push("active");
      nextClasses.push("hover");
    } else {
      nextClasses.push("inactive");
    }

    if (hover) {
      previousClasses.push("visible");
      nextClasses.push("visible");
    }

    return [
      <button
        key="left-button"
        className={previousClasses.join(" ")}
        onClick={e => onLeftClick(e, isPreviousActive)}
        aria-label="Previous slide"
        aria-disabled={!isPreviousActive}
      >
        <LeftIcon className="centered icon-left-d" />
      </button>,
      <button
        key="right-button"
        className={nextClasses.join(" ")}
        onClick={e => onRightClick(e, isNextActive)}
        aria-label="Next slide"
        aria-disabled={!isNextActive}
      >
        <RightIcon className="centered icon-right-d" />
      </button>,
    ];
  };

  const [items, count] = getItems() as [ReactNode, number];
  const buttons = hasButtons ? getButtons(items, count) : null;

  const classNames = ["swiper-container"];

  if (className) {
    classNames.push(className);
  }

  return (
    <div
      ref={div => {
        divRef.current = div;
        if (nodeRef) {
          nodeRef.current = div;
        }
      }}
      className={classNames.join(" ")}
      style={style}
    >
      <div className="swiper-wrapper">{items}</div>
      {buttons}
    </div>
  );
}
